import {
  InfoCircleOutlined,
  InfoCircleTwoTone,
  LoadingOutlined,
} from "@ant-design/icons";
import {
  ACCESS_TYPE_BLOCK_ID,
  RESOURCE_SELECTOR_BLOCK_ID,
} from "@p0security/shared/integrations/notifiers/constants";
import {
  GetRequestModalRequestBody,
  GetRequestModalResponseBody,
  GetSuggestionsRequestBody,
  GetSuggestionsResponseBody,
  SubmitRequestRequestBody,
  SubmitRequestResponseBody,
  WebAction,
  WebAlertBlock,
  WebBlock,
  WebDynamicSelectBlock,
  WebInputBlock,
  WebInputChoice,
  WebModalState,
  WebStaticSelectBlock,
  WebToggleBlock,
} from "@p0security/shared/integrations/notifiers/web-requests/types";
import { assertNever } from "@p0security/shared/types/index";
import {
  Alert,
  Button,
  Flex,
  Input,
  List,
  Modal,
  Select,
  Switch,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import { LinkWithEnvironment } from "components/Assessment/components/LinkWithEnvironment";
import { AuthFetch, useAuthFetch } from "components/Login/hook";
import { compact, isEqual, pick } from "lodash";
import { useCallback, useEffect, useState } from "react";
import {
  Controller,
  ControllerProps,
  ControllerRenderProps,
  FieldValues,
  FormProvider,
  useForm,
  useFormContext,
  useFormState,
  useWatch,
} from "react-hook-form";
import { spacing } from "styles/spacing";

import { SearchSelect } from "./SearchSelect";
import {
  MAX_SELECT_OPTIONS,
  createFormRules,
  createOptionGroups,
  filterDispatchBlocks,
  getBlockValues,
  parseFormError,
} from "./Utils";

type FetchOptionsArgs = { argId: string; query: string };

export const RequestAccessModal: React.FC<{
  open: boolean;
  onCancel: () => void;
}> = ({ open, onCancel }) => {
  const [modal, contextHolder] = Modal.useModal();
  const [initializing, setInitializing] = useState(true);
  const [dispatching, setDispatching] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [blocks, setBlocks] = useState<WebBlock[]>([]);
  const [actions, setActions] = useState<WebAction[]>([]);
  const [supportedResources, setSupportedResources] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [urls, setUrls] = useState<string[] | undefined>(undefined);

  const createAuthFetch = useAuthFetch(setError, setInitializing);
  const updateAuthFetch = useAuthFetch(setError);

  const form = useForm({ mode: "onChange" });
  const { control, reset, getValues } = form;
  const { isDirty } = useFormState({ control });
  const watching = useWatch({ control });

  const setForm = useCallback(
    (newValues: WebModalState) => {
      reset(newValues, {
        // keep the dirty state for modal confirmation before closing
        // and for the initial state of the form
        keepDirty: true,
      });
    },
    [reset]
  );

  const handleRequestAccess = useCallback(
    async (authFetch: AuthFetch, json: GetRequestModalRequestBody) => {
      try {
        setDispatching(true);
        const result = await authFetch(
          "integrations/web-requests/request-modal",
          {
            method: "POST",
            json,
          }
        );
        if (!result) {
          return setError("No response from server");
        }
        const data: GetRequestModalResponseBody = await result.json();

        if (!data.ok) {
          setError(data.error);
        } else {
          setForm(getBlockValues(data.blocks));
          setBlocks(data.blocks);
          setActions(data.actions);
          setSupportedResources(data.supportedResources);
        }
      } catch (error) {
        console.error("Request failed", error);
        setError("Error fetching options, check console for more information.");
      } finally {
        setDispatching(false);
      }
    },
    [setForm]
  );

  const handleFetchOptions = useCallback(
    async (args: FetchOptionsArgs) => {
      try {
        const json: GetSuggestionsRequestBody = {
          listerId: args.argId,
          query: args.query,
          values: watching,
        };
        const result = await updateAuthFetch(
          "integrations/web-requests/suggestions",
          {
            method: "POST",
            json,
          }
        );
        if (!result) {
          setError("No response from server. Please try again.");
          return [];
        }

        const data: GetSuggestionsResponseBody = await result.json();
        if (data.ok) {
          return data.suggestions;
        } else {
          setError(data.error);
          return [];
        }
      } catch (error) {
        console.error("Request failed", error);
        setError("Error fetching options, check console for more information.");
        return [];
      }
    },
    [updateAuthFetch, watching]
  );

  const handleClearError = useCallback(() => {
    setError(undefined);
  }, []);

  const handleSubmit = useCallback(
    async (data: WebModalState) => {
      setSubmitting(true);
      try {
        const json: SubmitRequestRequestBody = {
          values: data,
        };

        const response = await updateAuthFetch(
          "integrations/web-requests/submit",
          {
            method: "POST",
            json,
          }
        );
        if (!response?.ok) {
          return;
        }
        const result: SubmitRequestResponseBody = await response.json();
        if (result.ok) {
          setUrls(result.urls);
          reset();
        } else {
          setError(result.error);
        }
      } catch (error) {
        console.error(error);
        setError(
          "Error submitting request, check console for more information."
        );
      } finally {
        setSubmitting(false);
      }
    },
    [reset, updateAuthFetch]
  );

  useEffect(() => {
    if (open) {
      handleRequestAccess(createAuthFetch, { values: {} });
    }
  }, [open, handleRequestAccess, createAuthFetch]);

  useEffect(() => {
    if (initializing || dispatching || !isDirty) return;
    const previous = getBlockValues(blocks);
    if (previous.resource !== watching.resource) {
      reset({ resource: watching.resource });
    } else if (watching.accessType !== previous.accessType) {
      reset({ resource: watching.resource, accessType: watching.accessType });
    }
    let current = getValues();

    const blocksToCheck = blocks
      .filter(filterDispatchBlocks)
      .map((block) => block.id);

    const keysToCheck = Object.keys(current).filter((key) =>
      blocksToCheck.includes(key)
    );

    if (!keysToCheck.length) return;

    const changedKeys = compact(
      keysToCheck.filter((key) =>
        isEqual(current[key], previous[key]) ? undefined : key
      )
    );

    if (!changedKeys.length) return;

    // if the resources or access type changed, we need to reset the form
    if (changedKeys.includes(RESOURCE_SELECTOR_BLOCK_ID)) {
      current = pick(current, [RESOURCE_SELECTOR_BLOCK_ID]);
    } else if (changedKeys.includes(ACCESS_TYPE_BLOCK_ID)) {
      current = pick(current, [
        RESOURCE_SELECTOR_BLOCK_ID,
        ACCESS_TYPE_BLOCK_ID,
      ]);
    }

    handleRequestAccess(updateAuthFetch, {
      values: current,
    });
  }, [
    blocks,
    dispatching,
    isDirty,
    handleRequestAccess,
    initializing,
    updateAuthFetch,
    getValues,
    watching,
    reset,
  ]);

  const handleCloseModal = useCallback(() => {
    if (isDirty) {
      modal.confirm({
        title: "Discard changes?",
        content: "You have unsaved changes that will be lost.",
        okText: "Yes, discard changes",
        okType: "danger",
        maskClosable: true,
        onOk() {
          onCancel();
          reset();

          setBlocks([]);
          setActions([]);
          setInitializing(true);
          setError(undefined);
          setSubmitting(false);
          setDispatching(false);
        },
      });
    } else {
      onCancel();
    }
  }, [isDirty, modal, onCancel, reset]);

  const renderSubmittedRequests = useCallback((item: string) => {
    return (
      <div key={item}>
        Created a new access{" "}
        <LinkWithEnvironment to={item}>request</LinkWithEnvironment>
      </div>
    );
  }, []);

  return (
    <>
      <Modal
        title={
          <Flex>
            <Typography.Title level={2} style={{ padding: 0, margin: 0 }}>
              Request Access
            </Typography.Title>
          </Flex>
        }
        open={open}
        footer={null}
        onCancel={handleCloseModal}
        loading={initializing}
        maskClosable={false}
      >
        {submitting ? (
          <LoadingOutlined />
        ) : urls ? (
          <List dataSource={urls} renderItem={renderSubmittedRequests} />
        ) : (
          <FormProvider {...form}>
            <Typography.Paragraph type="secondary">
              {
                "Discover what resources and possible access modes you can request."
              }
              &nbsp;
              <Tooltip
                title={"Supported resources: " + supportedResources.join(", ")}
              >
                <InfoCircleTwoTone />
              </Tooltip>
            </Typography.Paragraph>
            <form onSubmit={form.handleSubmit(handleSubmit)}>
              <Flex vertical gap="middle">
                {error && (
                  <Alert
                    message="Error"
                    description={error}
                    type="error"
                    showIcon
                    closable
                    onClose={handleClearError}
                  />
                )}

                <BlocksRenderer
                  blocks={blocks}
                  onFetchOptions={handleFetchOptions}
                  watching={watching}
                />

                <ActionsRenderer
                  actions={actions}
                  onCancel={handleCloseModal}
                />
              </Flex>
            </form>
          </FormProvider>
        )}
      </Modal>
      {contextHolder}
    </>
  );
};

/** Check if a specific block has a selected value with beta flag */
const hasBlockBetaSelection = (
  block: WebBlock,
  values: WebModalState
): boolean => {
  if (block.type !== "static-select" && block.type !== "dynamic-select") {
    return false;
  }
  const selectedValue = values[block.id];
  if (!selectedValue) return false;

  // Handle both single and multivalued selections
  const selectedValues = Array.isArray(selectedValue)
    ? selectedValue
    : [selectedValue];

  // Check if any selected option has a non-GA maturity level
  for (const value of selectedValues) {
    const option = block.options?.find((opt) => opt.value === value);
    if (option?.maturity && option.maturity !== "ga") {
      return true;
    }
  }

  return false;
};

const BlocksRenderer: React.FC<{
  blocks: WebBlock[];
  onFetchOptions: (args: FetchOptionsArgs) => Promise<WebInputChoice[]>;
  watching: WebModalState;
}> = ({ blocks, onFetchOptions, watching }) => {
  return (
    <>
      {blocks.map((block) => {
        const hasNonGaMaturity = hasBlockBetaSelection(block, watching);

        return (
          <BlockRenderer
            key={block.id}
            block={block}
            onFetchOptions={onFetchOptions}
            hasNonGaMaturity={hasNonGaMaturity}
          />
        );
      })}
    </>
  );
};

const BlockRenderer: React.FC<{
  block: WebBlock;
  onFetchOptions: (args: FetchOptionsArgs) => Promise<WebInputChoice[]>;
  hasNonGaMaturity: boolean;
}> = ({ block, onFetchOptions, hasNonGaMaturity }) => {
  switch (block.type) {
    case "static-select":
      return (
        <StaticSelectRenderer
          block={block}
          hasNonGaMaturity={hasNonGaMaturity}
        />
      );
    case "alert":
      return <AlertRenderer block={block} />;
    case "input":
      return <InputRenderer block={block} />;
    case "dynamic-select":
      return (
        <DynamicSelectRenderer
          block={block}
          onFetchOptions={onFetchOptions}
          hasNonGaMaturity={hasNonGaMaturity}
        />
      );
    case "toggle":
      return <ToggleRenderer block={block} />;
    default:
      assertNever(block);
      return <>Unsupported block type</>;
  }
};

const BlockLayout: React.FC<{
  id: string;
  label: string;
  hint?: string;
  required?: boolean;
  multivalued?: boolean;
  hidden?: boolean;
  hasNonGaMaturity?: boolean;
  children: React.ReactNode | React.ReactNode[];
}> = ({
  id,
  children,
  label,
  hint,
  required,
  multivalued,
  hidden,
  hasNonGaMaturity,
}) => {
  const { control } = useFormContext();
  const { errors } = useFormState({ control });
  const error = parseFormError(errors[id]);
  if (hidden) return null;
  return (
    <Flex vertical gap="small">
      <div>
        <Flex align="center" style={{ gap: spacing.scale.xs }}>
          <Typography.Title level={5} style={{ marginBottom: 0 }}>
            {label}
          </Typography.Title>

          {hasNonGaMaturity && (
            <Tag color="blue" bordered={false} icon={<InfoCircleOutlined />}>
              Beta
            </Tag>
          )}

          {required ? (
            <Typography.Text type="danger">*</Typography.Text>
          ) : (
            <Typography.Text type="secondary">(optional)</Typography.Text>
          )}
        </Flex>
        {multivalued && (
          <Typography.Text type="secondary">
            You can select up to {MAX_SELECT_OPTIONS} items.
          </Typography.Text>
        )}
      </div>
      {children}
      {error ? (
        <Typography.Text type="danger">{error}</Typography.Text>
      ) : hint ? (
        <Typography.Text type="secondary">{hint}</Typography.Text>
      ) : null}
    </Flex>
  );
};

const ToggleRenderer: React.FC<{
  block: WebToggleBlock;
}> = ({ block }) => {
  const { control } = useFormContext();
  const handleChange = useCallback(
    (field: ControllerRenderProps<FieldValues, string>) => (checked: boolean) =>
      field.onChange(checked ? "true" : "false"),
    []
  );

  const renderToggle = useCallback<ControllerProps["render"]>(
    ({ field }) => (
      <Switch
        {...field}
        value={field.value === "true"}
        onChange={handleChange(field)}
        style={{
          width: "50px",
        }}
      />
    ),
    [handleChange]
  );

  return (
    <BlockLayout {...block}>
      <Controller
        name={block.id}
        control={control}
        render={renderToggle}
        rules={createFormRules(block)}
      />
    </BlockLayout>
  );
};

const DynamicSelectRenderer: React.FC<{
  block: WebDynamicSelectBlock;
  onFetchOptions: (args: FetchOptionsArgs) => Promise<WebInputChoice[]>;
  hasNonGaMaturity: boolean;
}> = ({ block, onFetchOptions, hasNonGaMaturity }) => {
  const { control } = useFormContext();
  const { errors } = useFormState({ control });

  const handleFetchOptions = useCallback(
    async (search: string): Promise<WebInputChoice[]> => {
      return onFetchOptions({ argId: block.id, query: search });
    },
    [block.id, onFetchOptions]
  );
  // SSH groups use minCharsForSearch of 2, all others use 3
  const isSshGroup = block.id.startsWith("ssh") && block.id.includes("group");
  const renderSelect = useCallback<ControllerProps["render"]>(
    ({ field }) => (
      <SearchSelect
        {...field}
        placeholder={block.placeholder}
        value={block.value}
        mode={block.multivalued ? "multiple" : undefined}
        fetchOptions={handleFetchOptions}
        status={errors[block.id] ? "error" : undefined}
        minCharsForSearch={isSshGroup ? 2 : undefined}
      />
    ),
    [
      block.id,
      block.multivalued,
      block.placeholder,
      block.value,
      errors,
      handleFetchOptions,
      isSshGroup,
    ]
  );

  return (
    <BlockLayout {...block} hasNonGaMaturity={hasNonGaMaturity}>
      <Controller
        name={block.id}
        control={control}
        render={renderSelect}
        rules={createFormRules(block)}
      />
    </BlockLayout>
  );
};

const StaticSelectRenderer: React.FC<{
  block: WebStaticSelectBlock;
  hasNonGaMaturity: boolean;
}> = ({ block, hasNonGaMaturity }) => {
  const { control } = useFormContext();
  const { errors } = useFormState({ control });

  const renderSelect = useCallback<ControllerProps["render"]>(
    ({ field }) => (
      <Select
        {...field}
        showSearch
        filterOption={true}
        optionFilterProp="filterLabel"
        placeholder={block.placeholder}
        value={block.value}
        mode={block.multivalued ? "multiple" : undefined}
        status={errors[block.id] ? "error" : undefined}
        allowClear
        options={createOptionGroups(block.options)}
      />
    ),
    [
      block.id,
      block.multivalued,
      block.options,
      block.placeholder,
      block.value,
      errors,
    ]
  );

  return (
    <BlockLayout {...block} hasNonGaMaturity={hasNonGaMaturity}>
      <Controller
        name={block.id}
        control={control}
        render={renderSelect}
        rules={createFormRules(block)}
      />
    </BlockLayout>
  );
};

const InputRenderer: React.FC<{
  block: WebInputBlock;
}> = ({ block }) => {
  const { control } = useFormContext();
  const { errors } = useFormState({ control });

  const renderInput = useCallback<ControllerProps["render"]>(
    ({ field }) => {
      const Component = block.multiline ? Input.TextArea : Input;
      return (
        <Component
          {...field}
          {...(block.multiline ? { rows: 4 } : {})}
          placeholder={block.placeholder}
          status={errors[block.id] ? "error" : undefined}
        />
      );
    },
    [block.multiline, block.placeholder, block.id, errors]
  );

  return (
    <BlockLayout {...block}>
      <Controller
        name={block.id}
        control={control}
        render={renderInput}
        rules={createFormRules(block)}
      />
    </BlockLayout>
  );
};

const AlertRenderer: React.FC<{ block: WebAlertBlock }> = ({ block }) => {
  return (
    <Alert
      message={block.label}
      description={block.placeholder}
      type={block.variant}
      showIcon
    />
  );
};

const ActionsRenderer: React.FC<{
  actions: WebAction[];
  onCancel: () => void;
}> = ({ actions, onCancel }) => {
  return (
    <Flex justify="flex-end" gap="small">
      {actions.map((action) => {
        return (
          <ActionRenderer key={action.id} action={action} onCancel={onCancel} />
        );
      })}
    </Flex>
  );
};

const ActionRenderer: React.FC<{
  action: WebAction;
  onCancel: () => void;
}> = ({ action, onCancel }) => {
  switch (action.type) {
    case "cancel":
      return (
        <Button onClick={onCancel} type="default" htmlType="button">
          {action.label}
        </Button>
      );
    case "submit":
      return (
        <Button type="primary" htmlType="submit">
          {action.label}
        </Button>
      );
    default:
      assertNever(action.type);
      return <>Unsupported action type</>;
  }
};
